import { appApi } from "../../core/api/app.js";
import { tagTone } from "../../core/format/tags.js";
import { dayLabelFull, money } from "../../core/format/periods.js";
import { useAppSession } from "../../state/AppSessionContext.jsx";
import { useEndpoint } from "../../state/useEndpoint.js";
import { Empty, Failed, Loading, Panel } from "../components/Panel.jsx";

function Score({ value }) {
  if (!Number.isFinite(value)) return <span className="score is-blank">—</span>;
  return (
    <span className="score">
      {[1, 2, 3, 4, 5].map((n) => (
        <i key={n} className={n <= value ? "on" : ""} />
      ))}
    </span>
  );
}

function DayCard({ day, today }) {
  const p = day.productivity;
  const r = day.ratings;

  return (
    <div className="daycard">
      <div className="daycard-head">
        <span className="daycard-day">{dayLabelFull(day.date, today)}</span>
        <span className="daycard-date">{day.date}</span>
      </div>

      {day.headline ? <p className="daycard-headline">{day.headline}</p> : <Empty>No summary for this day.</Empty>}

      {p && (
        <div className="daycard-row">
          <span className="daycard-key">Plan</span>
          <span className="daycard-val">
            {Number.isFinite(p.followedPct) ? `${p.followedPct}% followed` : p.verdict ?? "—"}
            {p.score ? <Score value={p.score} /> : null}
          </span>
        </div>
      )}

      {r && (
        <>
          <div className="daycard-row">
            <span className="daycard-key">Mood</span>
            <span className="daycard-val"><Score value={r.mood?.score} /></span>
          </div>
          <div className="daycard-row">
            <span className="daycard-key">Health</span>
            <span className="daycard-val"><Score value={r.health?.score} /></span>
          </div>
        </>
      )}

      {day.followThrough && (
        <div className="daycard-follow">
          <span className="daycard-key">Planned vs actual</span>
          <p className="daycard-note">{day.followThrough}</p>
        </div>
      )}
    </div>
  );
}

export default function Today() {
  const { token, me } = useAppSession();
  const { data, loading, error } = useEndpoint(() => appApi.overview(token), [token]);

  if (loading) return <Loading />;
  if (error) return <Failed error={error} />;
  if (!data) return null;

  const currency = data.currency ?? me?.currency;
  const last = data.lastDay;

  return (
    <>
      <Panel title="Today's schedule" meta={data.schedule?.day ?? data.today}>
        {!data.schedule || data.schedule.slots.length === 0 ? (
          <Empty>No schedule locked in for today.</Empty>
        ) : (
          <ol className="timeline">
            {data.schedule.slots.map((slot) => (
              <li key={slot.slotId ?? `${slot.startTime}-${slot.title}`} className="slot">
                <span className="slot-time">
                  {slot.startTime}–{slot.endTime}
                </span>
                <span className="slot-title">{slot.title}</span>
                {slot.status && <span className="slot-status">{slot.status}</span>}
              </li>
            ))}
          </ol>
        )}
      </Panel>

      <Panel title="Previous days' summaries">
        {data.recentDays.length === 0 ? (
          <Empty>No day summaries yet. They are written after the night check-in.</Empty>
        ) : (
          <div className="daygrid">
            {data.recentDays.map((day) => (
              <DayCard key={day.date} day={day} today={data.today} />
            ))}
          </div>
        )}
      </Panel>

      <Panel title={`Yesterday's record`} meta={last.date}>
        <div className="triple">
          <div className="mini">
            <div className="mini-head">Money</div>
            {!last.expenses.logged ? (
              <Empty>Nothing logged.</Empty>
            ) : (
              <>
                <div className="mini-big">{money(last.expenses.total, currency)}</div>
                <div className="mini-sub">{last.expenses.count} entries</div>
                <ul className="mini-list">
                  {last.expenses.rows.map((row, i) => (
                    <li key={i}>
                      <span>{row.name}</span>
                      <span>{money(row.amount, currency)}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>

          <div className="mini">
            <div className="mini-head">Work</div>
            {!last.tasks.logged ? (
              <Empty>No wrap-up happened.</Empty>
            ) : (
              <>
                <div className="mini-big">{last.tasks.count}</div>
                <div className="mini-sub">tasks logged</div>
                <ul className="mini-list">
                  {last.tasks.rows.map((row, i) => (
                    <li key={i}>
                      <span>{row.title}</span>
                      <span>{row.actualFrom ? `${row.actualFrom}–${row.actualTo ?? "?"}` : ""}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>

          <div className="mini">
            <div className="mini-head">Food</div>
            {!last.diet.logged ? (
              <Empty>Nothing logged.</Empty>
            ) : (
              <>
                <div className="mini-big">{last.diet.totals?.caloriesConsumed ?? "—"}</div>
                <div className="mini-sub">kcal</div>
                <ul className="mini-list">
                  <li>
                    <span>Protein</span>
                    <span>{last.diet.totals?.protein ?? "—"} g</span>
                  </li>
                  <li>
                    <span>Carbs</span>
                    <span>{last.diet.totals?.carbs ?? "—"} g</span>
                  </li>
                  <li>
                    <span>Fat</span>
                    <span>{last.diet.totals?.fat ?? "—"} g</span>
                  </li>
                  <li>
                    <span>Meals</span>
                    <span>{last.diet.meals.join(", ") || "—"}</span>
                  </li>
                </ul>
              </>
            )}
          </div>
        </div>
      </Panel>

      <Panel title="Still pending" meta={`${data.pendingTasks.length} open`}>
        {data.pendingTasks.length === 0 ? (
          <Empty>Nothing pending.</Empty>
        ) : (
          <ul className="tasklist">
            {data.pendingTasks.map((task) => (
              <li key={task.id}>
                <span className="tasklist-title">{task.title}</span>
                {task.category && <span className={`pill tone-${tagTone(task.category)}`}>{task.category}</span>}
                {task.dueDate && <span className="tasklist-due">due {task.dueDate}</span>}
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </>
  );
}
